import React from 'react';
import { Image, Text, View, type StyleProp, type TextStyle } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import QRCode from 'react-native-qrcode-svg';

import type { PortfolioPhoto } from '@/types/portfolio';

import {
  CTA_ORANGE,
  darken,
  FORMAT_RATIO,
  PHOTO_TYPES,
  readable,
  type PostData,
  type PostForm,
} from './post-model';

/** O que o card precisa do formulário — o resto (etapa, legenda) não entra na imagem. */
export type PostCardSpec = Pick<
  PostForm,
  | 'type'
  | 'photoId'
  | 'kicker'
  | 'title'
  | 'subtitle'
  | 'highlight'
  | 'showPrice'
  | 'showQr'
  | 'style'
  | 'format'
>;

interface PostCardProps {
  spec: PostCardSpec;
  data: PostData;
  colors: [string, string];
  width: number;
  /** Miniatura (seletor de estilo): sem sombra, cantos menores. */
  flat?: boolean;
}

const FONT = {
  regular: 'Poppins_400Regular',
  medium: 'Poppins_500Medium',
  semibold: 'Poppins_600SemiBold',
  bold: 'Poppins_700Bold',
  extrabold: 'Poppins_800ExtraBold',
  serif: 'Fraunces_500Medium',
};

const CREAM = '#F6F1E9';
const CREAM_INK = '#1D1B18';

/**
 * Imagem do post. Tudo é medido em `u` (1% da largura), então a mesma árvore
 * serve para a miniatura, a prévia e a exportação em 1080px.
 */
export function PostCard({ spec, data, colors, width, flat }: PostCardProps) {
  const u = width / 100;
  const height = width / FORMAT_RATIO[spec.format];
  const photo = data.photos.find((p) => p.id === spec.photoId);
  const hasPhoto = PHOTO_TYPES.includes(spec.type) && !!photo;
  const ctx: Ctx = { spec, data, colors, u, photo, compact: spec.format !== 'story' };

  const frame = {
    width,
    height,
    overflow: 'hidden' as const,
    borderRadius: flat ? 2 * u : 3.6 * u,
  };

  if (spec.style === 'editorial') return <Editorial ctx={ctx} frame={frame} hasPhoto={hasPhoto} />;
  if (spec.style === 'frame') return <Frame ctx={ctx} frame={frame} hasPhoto={hasPhoto} />;
  return <Block ctx={ctx} frame={frame} hasPhoto={hasPhoto} />;
}

interface Ctx {
  spec: PostCardSpec;
  data: PostData;
  colors: [string, string];
  u: number;
  photo: PortfolioPhoto | undefined;
  /** Feed (4:5 e 1:1): menos altura, listas mais curtas e letra menor. */
  compact: boolean;
}

interface StyleProps {
  ctx: Ctx;
  frame: { width: number; height: number; overflow: 'hidden'; borderRadius: number };
  hasPhoto: boolean;
}

// --- peças -----------------------------------------------------------------

function T({ style, children, lines }: { style: StyleProp<TextStyle>; children: React.ReactNode; lines?: number }) {
  return (
    <Text numberOfLines={lines} style={[{ includeFontPadding: false }, style]}>
      {children}
    </Text>
  );
}

function Logo({ ctx, size, bg, fg }: { ctx: Ctx; size: number; bg: string; fg: string }) {
  const { data } = ctx;
  if (data.logoUrl) {
    return (
      <Image
        source={{ uri: data.logoUrl }}
        aria-hidden
        style={{ width: size, height: size, borderRadius: size / 2, backgroundColor: '#fff' }}
      />
    );
  }
  return (
    <View
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        backgroundColor: bg,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <T style={{ color: fg, fontFamily: FONT.extrabold, fontSize: size * 0.38 }}>
        {data.businessName.trim().charAt(0).toUpperCase() || '•'}
      </T>
    </View>
  );
}

function Qr({ ctx, size }: { ctx: Ctx; size: number }) {
  const { u, colors, data } = ctx;
  return (
    <View style={{ backgroundColor: '#fff', borderRadius: 2 * u, padding: 1.4 * u }}>
      <QRCode
        value={`${data.publicUrl}?utm_source=post_generator`}
        size={size}
        color={colors[0]}
        backgroundColor="#FFFFFF"
      />
    </View>
  );
}

function PhotoMedia({ ctx, radius = 0 }: { ctx: Ctx; radius?: number }) {
  const { photo, u, spec, colors } = ctx;
  if (!photo) return null;
  const tag = (label: string, accent: boolean) => (
    <View
      style={{
        position: 'absolute',
        bottom: 2.4 * u,
        left: 2.4 * u,
        backgroundColor: accent ? colors[1] : 'rgba(0,0,0,0.55)',
        borderRadius: 99 * u,
        paddingHorizontal: 2 * u,
        paddingVertical: 0.8 * u,
      }}
    >
      <T
        style={{
          color: accent ? readable(colors[0], colors[1]) : '#fff',
          fontFamily: FONT.semibold,
          fontSize: 2.4 * u,
          letterSpacing: 0.15 * u,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </T>
    </View>
  );

  if (spec.type === 'beforeAfter' && photo.beforeUrl) {
    return (
      <View style={{ flex: 1, flexDirection: 'row', gap: 0.6 * u, borderRadius: radius, overflow: 'hidden' }}>
        <View style={{ flex: 1 }}>
          <Image source={{ uri: photo.beforeUrl }} accessibilityLabel="Antes" style={{ flex: 1 }} resizeMode="cover" />
          {tag('Antes', false)}
        </View>
        <View style={{ flex: 1 }}>
          <Image source={{ uri: photo.url }} accessibilityLabel="Depois" style={{ flex: 1 }} resizeMode="cover" />
          {tag('Depois', true)}
        </View>
      </View>
    );
  }
  return (
    <Image
      source={{ uri: photo.url }}
      accessibilityLabel="Foto do trabalho"
      style={{ flex: 1, borderRadius: radius, backgroundColor: '#2a2420' }}
      resizeMode="cover"
    />
  );
}

function Slots({ ctx, rowBg, pillBg, pillFg, fg }: { ctx: Ctx; rowBg: string; pillBg: string; pillFg: string; fg: string }) {
  const { u, data, compact } = ctx;
  const days = data.slots.slice(0, compact ? 3 : 4);
  if (!days.length) {
    return (
      <T style={{ color: fg, fontFamily: FONT.medium, fontSize: 3.4 * u, textAlign: 'center' }}>
        Agenda cheia essa semana
      </T>
    );
  }
  return (
    <View style={{ gap: 1.6 * u, width: '100%' }}>
      {days.map((d) => (
        <View
          key={d.weekday}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 2.4 * u,
            paddingHorizontal: 3 * u,
            paddingVertical: 2.2 * u,
            borderRadius: 3 * u,
            backgroundColor: rowBg,
          }}
        >
          <T style={{ color: fg, fontFamily: FONT.bold, fontSize: 3.2 * u, width: 12 * u }}>{d.weekday}</T>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 1.4 * u, flex: 1 }}>
            {d.times.map((t) => (
              <View
                key={t}
                style={{
                  backgroundColor: pillBg,
                  borderRadius: 99 * u,
                  paddingHorizontal: 2.2 * u,
                  paddingVertical: 0.8 * u,
                }}
              >
                <T style={{ color: pillFg, fontFamily: FONT.semibold, fontSize: 3 * u }}>{t}</T>
              </View>
            ))}
          </View>
        </View>
      ))}
    </View>
  );
}

function ServiceList({ ctx, fg }: { ctx: Ctx; fg: string }) {
  const { u, data, compact } = ctx;
  const items = data.services.filter((s) => s.price).slice(0, compact ? 3 : 4);
  return (
    <View style={{ width: '100%' }}>
      {items.map((s) => (
        <View
          key={s.id}
          style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 2 * u, paddingVertical: 1.8 * u }}
        >
          <T lines={1} style={{ color: fg, fontFamily: FONT.regular, fontSize: 3.3 * u, flexShrink: 1 }}>
            {s.name}
          </T>
          <View
            style={{
              flex: 1,
              borderBottomWidth: Math.max(1, 0.3 * u),
              borderStyle: 'dotted',
              borderColor: fg,
              opacity: 0.35,
              marginBottom: 1 * u,
            }}
          />
          <T style={{ color: fg, fontFamily: FONT.bold, fontSize: 3.3 * u }}>{s.price}</T>
        </View>
      ))}
    </View>
  );
}

function BigHighlight({ ctx, color, size }: { ctx: Ctx; color: string; size: number }) {
  const { u, spec } = ctx;
  return (
    <T
      lines={1}
      style={{
        color,
        fontFamily: FONT.extrabold,
        fontSize: size * u,
        lineHeight: size * u,
        letterSpacing: -0.03 * size * u,
        textAlign: 'center',
      }}
    >
      {spec.highlight || '%'}
    </T>
  );
}

function Kicker({ ctx, color }: { ctx: Ctx; color: string }) {
  const { u, spec } = ctx;
  if (!spec.kicker) return null;
  return (
    <T
      lines={1}
      style={{
        color,
        fontFamily: FONT.semibold,
        fontSize: 2.8 * u,
        letterSpacing: 0.4 * u,
        textTransform: 'uppercase',
      }}
    >
      {spec.kicker}
    </T>
  );
}

// --- EDITORIAL: foto sangrada com degradê, texto sempre branco, serifa ------

function Editorial({ ctx, frame, hasPhoto }: StyleProps) {
  const { spec, data, colors, u, compact } = ctx;
  const shadow: TextStyle = {
    textShadowColor: 'rgba(0,0,0,0.35)',
    textShadowOffset: { width: 0, height: 0.3 * u },
    textShadowRadius: 1.2 * u,
  };
  const accentOnBrand = readable(colors[1], colors[0]);

  let panel: React.ReactNode = null;
  if (!hasPhoto) {
    if (spec.type === 'openSlots') {
      panel = <Slots ctx={ctx} rowBg="rgba(255,255,255,0.12)" pillBg="#fff" pillFg={colors[0]} fg="#fff" />;
    } else if (spec.type === 'card') {
      panel = (
        <View style={{ alignItems: 'center', gap: 4 * u, width: '100%' }}>
          {compact ? null : <Logo ctx={ctx} size={20 * u} bg={colors[1]} fg={colors[0]} />}
          <ServiceList ctx={ctx} fg="#fff" />
        </View>
      );
    } else if (spec.type === 'promo') {
      panel = <BigHighlight ctx={ctx} color={accentOnBrand} size={compact ? 26 : 34} />;
    }
  }

  return (
    <View style={[frame, { backgroundColor: '#111' }]}>
      {hasPhoto ? (
        <>
          <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, flexDirection: 'row' }}>
            <PhotoMedia ctx={ctx} />
          </View>
          <LinearGradient
            colors={['rgba(0,0,0,0)', 'rgba(0,0,0,0.25)', 'rgba(0,0,0,0.72)', 'rgba(0,0,0,0.92)']}
            locations={[0.28, 0.42, 0.7, 1]}
            style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
          />
          <LinearGradient
            colors={['rgba(0,0,0,0.55)', 'rgba(0,0,0,0)']}
            locations={[0, 0.24]}
            style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
          />
        </>
      ) : (
        <LinearGradient
          colors={[colors[0], darken(colors[0], 0.45)]}
          start={{ x: 0.8, y: 0 }}
          end={{ x: 0.2, y: 1 }}
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
        />
      )}

      <View style={{ flex: 1, padding: 6 * u, justifyContent: 'space-between' }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 3 * u }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 2.4 * u, flexShrink: 1 }}>
            <Logo ctx={ctx} size={9 * u} bg="#fff" fg={colors[0]} />
            <View style={{ flexShrink: 1 }}>
              <T lines={1} style={[shadow, { color: '#fff', fontFamily: FONT.bold, fontSize: 3.6 * u }]}>
                {data.businessName}
              </T>
              {data.category ? (
                <T lines={1} style={[shadow, { color: 'rgba(255,255,255,0.8)', fontFamily: FONT.regular, fontSize: 2.6 * u }]}>
                  {data.category}
                </T>
              ) : null}
            </View>
          </View>
          {spec.type === 'service' && spec.showPrice && spec.highlight ? (
            <View style={{ backgroundColor: colors[1], borderRadius: 99 * u, paddingHorizontal: 3.6 * u, paddingVertical: 1.8 * u }}>
              <T style={{ color: readable(colors[0], colors[1]), fontFamily: FONT.bold, fontSize: 3.4 * u }}>
                {spec.highlight}
              </T>
            </View>
          ) : null}
        </View>

        {panel ? (
          <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', paddingVertical: 4 * u, overflow: 'hidden' }}>
            {panel}
          </View>
        ) : null}

        <View style={{ gap: 2.4 * u }}>
          {spec.kicker ? (
            <View
              style={{
                alignSelf: 'flex-start',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1.6 * u,
                backgroundColor: 'rgba(255,255,255,0.16)',
                borderRadius: 99 * u,
                paddingLeft: 2.2 * u,
                paddingRight: 2.8 * u,
                paddingVertical: 1.2 * u,
              }}
            >
              <View style={{ width: 1.8 * u, height: 1.8 * u, borderRadius: u, backgroundColor: colors[1] }} />
              <Kicker ctx={ctx} color="#fff" />
            </View>
          ) : null}
          <T
            lines={3}
            style={[shadow, { color: '#fff', fontFamily: FONT.serif, fontSize: (compact ? 8.4 : 10) * u, lineHeight: (compact ? 9 : 10.6) * u, letterSpacing: -0.2 * u }]}
          >
            {spec.title}
          </T>
          {spec.subtitle ? (
            <T lines={2} style={[shadow, { color: 'rgba(255,255,255,0.92)', fontFamily: FONT.regular, fontSize: 3.4 * u }]}>
              {spec.subtitle}
            </T>
          ) : null}
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 3 * u,
              marginTop: 1.6 * u,
              paddingTop: 3.4 * u,
              borderTopWidth: Math.max(1, 0.3 * u),
              borderTopColor: 'rgba(255,255,255,0.25)',
            }}
          >
            <View style={{ backgroundColor: CTA_ORANGE, borderRadius: 99 * u, paddingHorizontal: 3.6 * u, paddingVertical: 2 * u, flexShrink: 1 }}>
              <T lines={1} style={{ color: '#fff', fontFamily: FONT.semibold, fontSize: 3 * u }}>
                Agende em {data.displayUrl} →
              </T>
            </View>
            {spec.showQr ? <Qr ctx={ctx} size={11 * u} /> : null}
          </View>
        </View>
      </View>
    </View>
  );
}

// --- MOLDURA: papel creme, foto emoldurada, faixa da marca no rodapé --------

function Frame({ ctx, frame, hasPhoto }: StyleProps) {
  const { spec, data, colors, u, compact } = ctx;
  const brandOnCream = readable(colors[0], CREAM);
  const onBrand = readable('#FFFFFF', colors[0]);

  let media: React.ReactNode;
  const box = { flex: 1, borderRadius: 4 * u, overflow: 'hidden' as const };
  if (hasPhoto) {
    media = (
      <View style={[box, { flexDirection: 'row' }]}>
        <PhotoMedia ctx={ctx} />
      </View>
    );
  } else if (spec.type === 'promo') {
    media = (
      <View style={[box, { backgroundColor: colors[0], alignItems: 'center', justifyContent: 'center' }]}>
        <BigHighlight ctx={ctx} color={readable(colors[1], colors[0])} size={compact ? 22 : 30} />
      </View>
    );
  } else {
    media = (
      <View style={[box, { backgroundColor: '#fff', padding: 5 * u, justifyContent: 'center', alignItems: 'center', gap: 4 * u }]}>
        {spec.type === 'openSlots' ? (
          <Slots ctx={ctx} rowBg={CREAM} pillBg={colors[0]} pillFg={onBrand} fg={CREAM_INK} />
        ) : (
          <>
            <ServiceList ctx={ctx} fg={CREAM_INK} />
            {spec.showQr && !compact ? <Qr ctx={ctx} size={16 * u} /> : null}
          </>
        )}
      </View>
    );
  }

  const showFootQr = spec.showQr && spec.type !== 'card';

  return (
    <View style={[frame, { backgroundColor: CREAM, padding: 6 * u, paddingBottom: 0, gap: 4 * u }]}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 2.4 * u }}>
        <Logo ctx={ctx} size={9 * u} bg={colors[0]} fg={onBrand} />
        <View style={{ flexShrink: 1 }}>
          <T lines={1} style={{ color: CREAM_INK, fontFamily: FONT.bold, fontSize: 3.6 * u }}>
            {data.businessName}
          </T>
          {data.place ? (
            <T lines={1} style={{ color: '#6D665C', fontFamily: FONT.regular, fontSize: 2.6 * u }}>
              {data.place}
            </T>
          ) : null}
        </View>
      </View>

      {media}

      <View style={{ gap: 1.6 * u }}>
        <Kicker ctx={ctx} color={brandOnCream} />
        <T
          lines={2}
          style={{
            color: CREAM_INK,
            fontFamily: FONT.bold,
            fontSize: (compact ? 7 : 8.4) * u,
            lineHeight: (compact ? 7.6 : 9) * u,
            letterSpacing: -0.25 * u,
          }}
        >
          {spec.title}
        </T>
        {spec.subtitle || (spec.type === 'service' && spec.showPrice && spec.highlight) ? (
          <T lines={2} style={{ color: '#6D665C', fontFamily: FONT.regular, fontSize: 3.3 * u }}>
            {spec.subtitle}
            {spec.type === 'service' && spec.showPrice && spec.highlight ? (
              <T style={{ color: brandOnCream, fontFamily: FONT.bold, fontSize: 3.3 * u }}>
                {spec.subtitle ? ' · ' : ''}
                {spec.highlight}
              </T>
            ) : null}
          </T>
        ) : null}
      </View>

      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 3 * u,
          backgroundColor: colors[0],
          marginHorizontal: -6 * u,
          paddingHorizontal: 6 * u,
          paddingVertical: 4 * u,
        }}
      >
        <T lines={1} style={{ color: onBrand, fontFamily: FONT.regular, fontSize: 3.2 * u, flexShrink: 1 }}>
          Agende em <T style={{ fontFamily: FONT.bold }}>{data.displayUrl}</T>
        </T>
        {showFootQr ? (
          <Qr ctx={ctx} size={9 * u} />
        ) : (
          <T style={{ color: onBrand, fontFamily: FONT.bold, fontSize: 4 * u }}>→</T>
        )}
      </View>
    </View>
  );
}

// --- BLOCO: cor da marca chapada, letra gigante, foto em arco ---------------

function Block({ ctx, frame, hasPhoto }: StyleProps) {
  const { spec, data, colors, u, compact } = ctx;
  const accent = readable(colors[1], colors[0]);
  const onAccent = readable(colors[0], colors[1]);
  const titleSize = spec.format === 'square' ? 9 : spec.format === 'portrait' ? 10 : 12;

  let media: React.ReactNode;
  if (hasPhoto) {
    media = (
      <View
        style={{
          flex: 1,
          flexDirection: 'row',
          borderTopLeftRadius: 40 * u,
          borderTopRightRadius: 40 * u,
          borderBottomLeftRadius: 4 * u,
          borderBottomRightRadius: 4 * u,
          overflow: 'hidden',
          backgroundColor: 'rgba(255,255,255,0.1)',
        }}
      >
        <PhotoMedia ctx={ctx} />
      </View>
    );
  } else if (spec.type === 'openSlots') {
    media = (
      <View style={{ flex: 1, justifyContent: 'center' }}>
        <Slots ctx={ctx} rowBg="rgba(255,255,255,0.12)" pillBg={colors[1]} pillFg={onAccent} fg="#fff" />
      </View>
    );
  } else if (spec.type === 'card') {
    media = (
      <View style={{ flex: 1, justifyContent: 'center' }}>
        <ServiceList ctx={ctx} fg="#fff" />
      </View>
    );
  } else {
    media = (
      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
        <BigHighlight ctx={ctx} color={accent} size={compact ? 30 : 38} />
      </View>
    );
  }

  const sticker =
    spec.type === 'service' && spec.showPrice && spec.highlight ? (
      <View
        style={{
          position: 'absolute',
          right: 3 * u,
          top: '30%',
          zIndex: 2,
          width: 22 * u,
          height: 22 * u,
          borderRadius: 11 * u,
          backgroundColor: colors[1],
          alignItems: 'center',
          justifyContent: 'center',
          transform: [{ rotate: '-10deg' }],
        }}
      >
        <T style={{ color: onAccent, fontFamily: FONT.extrabold, fontSize: 4.6 * u }}>{spec.highlight}</T>
      </View>
    ) : null;

  return (
    <View style={[frame, { backgroundColor: colors[0], padding: 6 * u, gap: 3 * u }]}>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 3 * u }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 2.4 * u, flexShrink: 1 }}>
          <Logo ctx={ctx} size={9 * u} bg={colors[1]} fg={onAccent} />
          <T lines={1} style={{ color: '#fff', fontFamily: FONT.bold, fontSize: 3.6 * u, flexShrink: 1 }}>
            {data.businessName}
          </T>
        </View>
        <View style={{ flexShrink: 1 }}>
          <Kicker ctx={ctx} color={accent} />
        </View>
      </View>

      <T
        lines={3}
        style={{
          color: '#fff',
          fontFamily: FONT.extrabold,
          fontSize: titleSize * u,
          lineHeight: titleSize * 0.95 * u,
          letterSpacing: -0.03 * titleSize * u,
          textTransform: 'uppercase',
        }}
      >
        {spec.title}
      </T>

      <View style={{ flex: 1, minHeight: 0 }}>
        {sticker}
        {media}
      </View>

      {spec.subtitle ? (
        <T lines={2} style={{ color: 'rgba(255,255,255,0.85)', fontFamily: FONT.regular, fontSize: 3.4 * u }}>
          {spec.subtitle}
        </T>
      ) : null}

      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 3 * u }}>
        <View style={{ backgroundColor: colors[1], borderRadius: 99 * u, paddingHorizontal: 3.6 * u, paddingVertical: 2 * u, flexShrink: 1 }}>
          <T lines={1} style={{ color: onAccent, fontFamily: FONT.semibold, fontSize: 3 * u }}>
            {data.displayUrl} →
          </T>
        </View>
        {spec.showQr ? <Qr ctx={ctx} size={10 * u} /> : null}
      </View>
    </View>
  );
}
